
// src/types/index.js - Standardized data structures
import { Timestamp } from 'firebase/firestore';
import logger from '../shared/utils/core/logger';


// NOTE: COLLECTIONS constant is defined in src/shared/utils/core/firebase-config.js
// Import from there to avoid duplication

// Entry schema validation
/** Strip HTML tags and return first 200 chars as a plain-text preview. */
function buildPreview(html) {
  if (!html) return '';
  const plain = html.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
  return plain.length <= 200 ? plain : plain.substring(0, 200).trimEnd() + '…';
}

export const createJournalEntry = ({
  uid,
  content,
  mood = null,
  habits = [],
  weather = null,
  mode = 'general',
  imageUrl = null,
  aiResponse = null,
  extractedText = null,
  isDraft = false,
  // OCR & Handwriting Analysis fields
  ocrMetadata = null,
  originalImages = null,
  handwritingAnalysis = null,
  detectedDate = null,
  pageCount = null,
  // Writing session metadata (Phase 3 data collection)
  wordCount = null,
  writingDuration = null
}) => ({
  uid: validateString(uid, 'uid'),
  content: validateString(content, 'content', 1, 50000), // Increased for multi-page
  preview: buildPreview(content),
  mood: mood ? validateString(mood, 'mood', 1, 50) : null,
  habits: Array.isArray(habits) ? habits.map(h => validateString(h, 'habit', 1, 100)) : [],
  weather: weather ? validateString(weather, 'weather', 1, 50) : null,
  mode: validateString(mode, 'mode', 1, 50),
  imageUrl: imageUrl ? validateUrl(imageUrl) : null,
  aiResponse: aiResponse ? validateString(aiResponse, 'aiResponse', 1, 2000) : null,
  extractedText: extractedText ? validateString(extractedText, 'extractedText', 1, 50000) : null,
  // OCR & Handwriting Analysis - stored for insights and "View Original"
  ocrMetadata: ocrMetadata ? validateObject(ocrMetadata) : null,
  originalImages: Array.isArray(originalImages) ? originalImages : null, // Base64 or URLs
  handwritingAnalysis: handwritingAnalysis ? validateObject(handwritingAnalysis) : null,
  detectedDate: detectedDate ? validateObject(detectedDate) : null,
  pageCount: typeof pageCount === 'number' ? pageCount : null,
  wordCount: Number.isFinite(wordCount) && wordCount >= 0 ? Math.round(wordCount) : null,
  writingDuration: Number.isFinite(writingDuration) && writingDuration > 0 ? writingDuration : null,
  isDraft: typeof isDraft === 'boolean' ? isDraft : false,
  // Use current timestamp for optimistic UI, but server will overwrite with serverTimestamp
  // This ensures entries display immediately while maintaining server-side consistency
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now(),
  version: 2 // Bumped version for new schema
});

export const createGoal = ({
  uid,
  title,
  description = '',
  goalType = 'habit',
  targetType = 'frequency',
  targetValue = 1,
  timeframe = 'daily',
  trackingSource = 'manual',
  category = 'other',
  startDate = null,
  endDate = null,
  milestones = null,
}) => ({
  uid: validateString(uid, 'uid'),
  title: validateString(title, 'title', 1, 200),
  description: description ? validateString(description, 'description', 0, 500) : null,
  goalType: validateString(goalType, 'goalType', 1, 50),
  targetType: validateString(targetType, 'targetType', 1, 50),
  targetValue: validateNumber(targetValue, 'targetValue', 1, 999),
  timeframe: validateString(timeframe, 'timeframe', 1, 50),
  trackingSource: validateString(trackingSource, 'trackingSource', 1, 50),
  category: validateString(category, 'category', 1, 50),
  startDate: startDate || Timestamp.now(),
  endDate: endDate || null,
  milestones: Array.isArray(milestones) ? milestones : null,
  status: 'active',
  streak: 0,
  longestStreak: 0,
  lastCompleted: null,
  lastChecked: null,
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now()
});

export const createUserProfile = ({
  uid,
  displayName,
  email,
  birthDate = null,
  birthTime = null,
  birthPlace = null,
  timezone = Intl.DateTimeFormat().resolvedOptions().timeZone,
  preferences = {}
}) => ({
  uid: validateString(uid, 'uid'),
  displayName: validateString(displayName, 'displayName', 1, 100),
  email: validateEmail(email),
  birthDate: birthDate ? validateDate(birthDate) : null,
  birthTime: birthTime ? validateTime(birthTime) : null,
  birthPlace: birthPlace ? validateString(birthPlace, 'birthPlace', 1, 200) : null,
  timezone: validateString(timezone, 'timezone', 1, 100),
  preferences: validateObject(preferences),
  createdAt: Timestamp.now(),
  updatedAt: Timestamp.now(),
  onboardingCompleted: false,
  lastLoginAt: Timestamp.now()
});

// Validation functions
function validateString(value, fieldName, minLength = 0, maxLength = Infinity) {
  if (value === null || value === undefined) {
    if (minLength > 0) {
      throw new Error(`${fieldName} is required`);
    }
    return value;
  }
  
  if (typeof value !== 'string') {
    throw new Error(`${fieldName} must be a string`);
  }
  
  const trimmed = value.trim();
  if (trimmed.length < minLength) {
    throw new Error(`${fieldName} must be at least ${minLength} characters`);
  }
  if (trimmed.length > maxLength) {
    throw new Error(`${fieldName} must be no more than ${maxLength} characters`);
  }
  
  return trimmed;
}

function validateNumber(value, fieldName, min = -Infinity, max = Infinity) {
  if (typeof value !== 'number' || isNaN(value)) {
    throw new Error(`${fieldName} must be a valid number`);
  }
  if (value < min || value > max) {
    throw new Error(`${fieldName} must be between ${min} and ${max}`);
  }
  return value;
}

function validateEmail(email) {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  if (!emailRegex.test(email)) {
    throw new Error('Invalid email format');
  }
  return email.toLowerCase().trim();
}

function validateUrl(url) {
  try {
    new URL(url);
    return url;
  } catch {
    throw new Error('Invalid URL format');
  }
}

function validateDate(dateString) {
  const date = new Date(dateString);
  if (isNaN(date.getTime())) {
    throw new Error('Invalid date format');
  }
  return dateString;
}

function validateTime(timeString) {
  const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
  if (!timeRegex.test(timeString)) {
    throw new Error('Invalid time format (use HH:MM)');
  }
  return timeString;
}

function validateObject(obj) {
  if (obj === null || typeof obj !== 'object' || Array.isArray(obj)) {
    throw new Error('Must be a valid object');
  }
  return obj;
}

// Error types for consistent error handling
export class ValidationError extends Error {
  constructor(message, field = null) {
    super(message);
    this.name = 'ValidationError';
    this.field = field;
  }
}

export class NetworkError extends Error {
  constructor(message, status = null) {
    super(message);
    this.name = 'NetworkError';
    this.status = status;
  }
}

export class AuthError extends Error {
  constructor(message, code = null) {
    super(message);
    this.name = 'AuthError';
    this.code = code;
  }
}

// Migration utilities for existing data
export const migrateEntry = (oldEntry) => {
  try {
    // Handle old entry format
    return {
      id: oldEntry.id,
      uid: oldEntry.uid,
      content: oldEntry.content || oldEntry.text || '',
      mood: oldEntry.mood || null,
      habits: oldEntry.habits || [],
      weather: oldEntry.weather || null,
      mode: oldEntry.mode || 'general',
      imageUrl: oldEntry.imageUrl || null,
      aiResponse: oldEntry.feedback || oldEntry.ai || oldEntry.aiResponse || null,
      extractedText: oldEntry.extractedText || null,
      createdAt: oldEntry.createdAt || oldEntry.ts || Timestamp.now(),
      updatedAt: oldEntry.updatedAt || oldEntry.createdAt || oldEntry.ts || Timestamp.now(),
      version: 1
    };
  } catch (error) {
    logger.warn('Failed to migrate entry:', oldEntry, error);
    return null;
  }
};

// Status enums for UI states
export const LoadingState = {
  IDLE: 'idle',
  LOADING: 'loading',
  SUCCESS: 'success',
  ERROR: 'error'
};

export const SyncStatus = {
  SYNCED: 'synced',
  PENDING: 'pending',
  ERROR: 'error',
  OFFLINE: 'offline'
};